import React, { useState } from 'react';
import { ChevronDownIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { MessageType } from '@/types/system-message-templates';
import { getVariablesByGroup, VariableGroup } from '@/types/system-message-variables';

interface SystemVariableDropdownProps {
  messageType: MessageType;
  onInsert: (variableName: string) => void;
  className?: string;
}

const GROUP_LABELS: Record<VariableGroup, string> = {
  client: 'Client Info',
  practitioner: 'Practitioner Info',
  appointment: 'Appointment Info',
  payment: 'Payment Info',
};

export function SystemVariableDropdown({ messageType, onInsert, className = '' }: SystemVariableDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedVar, setCopiedVar] = useState<string | null>(null);

  const handleInsert = (variableName: string) => {
    onInsert(variableName);
    setIsOpen(false);

    // Show copied feedback
    setCopiedVar(variableName);
    setTimeout(() => setCopiedVar(null), 1500);
  };

  const variablesByGroup = getVariablesByGroup(messageType);

  // Filter out empty groups
  const nonEmptyGroups = (Object.entries(variablesByGroup) as [VariableGroup, typeof variablesByGroup[VariableGroup]][])
    .filter(([, variables]) => variables.length > 0);

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm font-medium text-gray-700"
      >
        <span>Insert Variable</span>
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-20 max-h-96 overflow-y-auto">
            <div className="p-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">
                Available Variables
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Click to insert into your template
              </p>
            </div>

            {nonEmptyGroups.map(([group, variables], index) => (
              <div key={group} className={`p-3 ${index < nonEmptyGroups.length - 1 ? 'border-b border-gray-100' : ''}`}>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  {GROUP_LABELS[group]}
                </h4>
                <div className="space-y-1">
                  {variables.map((variable) => (
                    <VariableItem
                      key={variable.name}
                      variableName={variable.name}
                      description={variable.description}
                      onInsert={handleInsert}
                      isCopied={copiedVar === variable.name}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

interface VariableItemProps {
  variableName: string;
  description: string;
  onInsert: (variableName: string) => void;
  isCopied: boolean;
}

function VariableItem({ variableName, description, onInsert, isCopied }: VariableItemProps) {
  return (
    <button
      type="button"
      onClick={() => onInsert(variableName)}
      className="w-full px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left flex items-start justify-between gap-2 group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <code className="text-xs font-mono bg-gray-100 text-[#5B8E87] px-2 py-0.5 rounded">
            {`{{${variableName}}}`}
          </code>
          {isCopied && (
            <span className="text-xs text-[#5B8E87] flex items-center gap-1">
              <CheckIcon className="w-3 h-3" />
              Inserted
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 truncate">{description}</p>
      </div>
      <CopyIcon className="w-4 h-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0 mt-1" />
    </button>
  );
}
